import { z } from 'zod';

// ============================================================================
// SETTING CATEGORIES
// ============================================================================

export const SettingCategory = {
  AUTH: 'auth',
  NOTIFICATIONS: 'notifications',
  BRANDING: 'branding',
  SYSTEM: 'system',
  TERM_SHEET: 'term_sheet',
} as const;

export type SettingCategoryType = (typeof SettingCategory)[keyof typeof SettingCategory];

// ============================================================================
// AUTH SETTINGS
// ============================================================================

export const MfaEnforcement = {
  OFF: 'off',
  OPTIONAL: 'optional',
  REQUIRED: 'required',
} as const;

export type MfaEnforcementType = (typeof MfaEnforcement)[keyof typeof MfaEnforcement];

export const AuthMethod = {
  EMAIL: 'email',
  GOOGLE: 'google',
  SSO: 'sso',
  GITHUB: 'github',
  MICROSOFT: 'microsoft',
} as const;

export type AuthMethodType = (typeof AuthMethod)[keyof typeof AuthMethod];

export const AuthSettingsSchema = z.object({
  sessionTimeoutMinutes: z.number().min(5).max(1440).default(480),
  passwordMinLength: z.number().min(6).max(128).default(8),
  passwordRequireUppercase: z.boolean().default(true),
  passwordRequireNumbers: z.boolean().default(true),
  passwordRequireSpecialChars: z.boolean().default(true),
  mfaEnforcement: z.enum(['off', 'optional', 'required']).default('optional'),
  allowedAuthMethods: z
    .array(z.enum(['email', 'google', 'sso', 'github', 'microsoft']))
    .default(['email', 'google', 'sso']),
  maxLoginAttempts: z.number().min(1).max(100).default(5),
});

export type AuthSettings = z.infer<typeof AuthSettingsSchema>;

export const UpdateAuthSettingsSchema = AuthSettingsSchema.partial();

export type UpdateAuthSettings = z.infer<typeof UpdateAuthSettingsSchema>;

// ============================================================================
// NOTIFICATIONS SETTINGS
// ============================================================================

export const NotificationSettingsSchema = z.object({
  emailEnabled: z.boolean().default(true),
  newSignupNotifications: z.boolean().default(true),
  usageAlerts: z.boolean().default(true),
  securityAlerts: z.boolean().default(true),
  weeklyDigest: z.boolean().default(false),
  webhookUrl: z.string().url().nullable().default(null),
  webhookEvents: z
    .array(
      z.enum([
        'user.created',
        'user.deleted',
        'agent.created',
        'agent.updated',
        'document.processed',
        'billing.invoice',
        'security.alert',
      ]),
    )
    .default([]),
  slackEnabled: z.boolean().default(false),
  slackWebhookUrl: z.string().url().nullable().default(null),
});

export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

export const UpdateNotificationSettingsSchema = NotificationSettingsSchema.partial();

export type UpdateNotificationSettings = z.infer<typeof UpdateNotificationSettingsSchema>;

// ============================================================================
// BRANDING SETTINGS
// ============================================================================

export const BrandingSettingsSchema = z.object({
  logoUrl: z.string().url().nullable().default(null),
  companyName: z.string().min(1).max(100).default('Ops Matrix'),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#1976d2'),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#dc004e'),
  customCss: z.string().max(50000).nullable().default(null),
  faviconUrl: z.string().url().nullable().default(null),
});

export type BrandingSettings = z.infer<typeof BrandingSettingsSchema>;

export const UpdateBrandingSettingsSchema = BrandingSettingsSchema.partial();

export type UpdateBrandingSettings = z.infer<typeof UpdateBrandingSettingsSchema>;

// ============================================================================
// SYSTEM SETTINGS
// ============================================================================

export const TimeFormat = {
  TWELVE_HOUR: '12h',
  TWENTY_FOUR_HOUR: '24h',
} as const;

export type TimeFormatType = (typeof TimeFormat)[keyof typeof TimeFormat];

export const DateFormat = {
  ISO: 'YYYY-MM-DD',
  US: 'MM/DD/YYYY',
  EU: 'DD/MM/YYYY',
  UK: 'DD-MM-YYYY',
} as const;

export type DateFormatType = (typeof DateFormat)[keyof typeof DateFormat];

export const SystemSettingsSchema = z.object({
  defaultTimezone: z.string().default('UTC'),
  dateFormat: z.enum(['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY']).default('YYYY-MM-DD'),
  timeFormat: z.enum(['12h', '24h']).default('24h'),
  language: z.string().min(2).max(5).default('en'),
  dataRetentionDays: z.number().min(30).max(3650).default(365),
  featureFlags: z.record(z.string(), z.boolean()).default({}),
});

export type SystemSettings = z.infer<typeof SystemSettingsSchema>;

export const UpdateSystemSettingsSchema = SystemSettingsSchema.partial();

export type UpdateSystemSettings = z.infer<typeof UpdateSystemSettingsSchema>;

// ============================================================================
// COMBINED SETTINGS RESPONSE
// ============================================================================

export const AllSettingsSchema = z.object({
  auth: AuthSettingsSchema,
  notifications: NotificationSettingsSchema,
  branding: BrandingSettingsSchema,
  system: SystemSettingsSchema,
});

export type AllSettings = z.infer<typeof AllSettingsSchema>;

// ============================================================================
// LOGO UPLOAD
// ============================================================================

export const LogoUploadResponseSchema = z.object({
  logoUrl: z.string().url(),
  message: z.string(),
});

export type LogoUploadResponse = z.infer<typeof LogoUploadResponseSchema>;

// ============================================================================
// SETTING ENTITY (from database)
// ============================================================================

export const SettingEntitySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid().nullable(),
  category: z.enum(['auth', 'notifications', 'branding', 'system']),
  key: z.string(),
  value: z.unknown(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SettingEntity = z.infer<typeof SettingEntitySchema>;

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface SettingsResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface TestWebhookResponse {
  success: boolean;
  statusCode?: number;
  responseTime?: number;
  message: string;
  error?: string;
}
